import { useState, useEffect, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactFlow, {
  Controls,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import { useAuth } from '../context/AuthContext';

const SCROLL_IMG = '/images/scroll.jpeg';
const NIGHT_BG = '/images/night-sky.jpeg';
const DAY_BG = '/images/day-sky.jpeg';

function isNightTime() {
  const hour = new Date().getHours();
  return hour < 6 || hour >= 18; // 6pm - 6am = night
}

const REACTFLOW_STYLES = `
.react-flow { width: 100%; height: 100%; position: relative; overflow: hidden; }
.react-flow__container { position: absolute; width: 100%; height: 100%; top: 0; left: 0; }
.react-flow__pane { z-index: 1; cursor: grab; }
.react-flow__pane.dragging { cursor: grabbing; }
.react-flow__viewport { transform-origin: 0 0; z-index: 2; pointer-events: none; }
.react-flow__renderer { z-index: 4; }
.react-flow .react-flow__edges { pointer-events: none; overflow: visible; position: absolute; z-index: 2; }
.react-flow__edge-path, .react-flow__connection-path { fill: none; }
.react-flow__edge { pointer-events: visibleStroke; cursor: pointer; }
.react-flow__edge.animated path { stroke-dasharray: 5; animation: dashdraw 0.5s linear infinite; }
.react-flow__controls { position: absolute; left: 10px; bottom: 10px; z-index: 5; display: flex; flex-direction: column; }
.react-flow__controls button { display: flex; justify-content: center; align-items: center; width: 26px; height: 26px; border: 1px solid #334155; background: #1e293b; cursor: pointer; color: #f59e0b; }
.react-flow__controls button:hover { background: #334155; }
.react-flow__controls button svg { width: 12px; height: 12px; fill: currentColor; }
.react-flow__background { position: absolute; width: 100%; height: 100%; top: 0; left: 0; }
.react-flow__node { position: absolute; user-select: none; pointer-events: all; transform-origin: 0 0; box-sizing: border-box; cursor: grab; }
.react-flow__node.dragging { cursor: grabbing; }
.react-flow__handle { position: absolute; pointer-events: none; min-width: 5px; min-height: 5px; width: 6px; height: 6px; background: transparent; border: none; border-radius: 100%; }
.react-flow__handle-bottom { top: auto; left: 50%; bottom: -4px; transform: translate(-50%, 0); }
.react-flow__handle-top { top: -4px; left: 50%; transform: translate(-50%, 0); }
.react-flow__handle-left { top: 50%; left: -4px; transform: translate(0, -50%); }
.react-flow__handle-right { top: 50%; right: -4px; transform: translate(0, -50%); }
.react-flow__panel { position: absolute; z-index: 5; margin: 15px; }
.react-flow__panel.top { top: 0; }
.react-flow__panel.bottom { bottom: 0; }
.react-flow__panel.left { left: 0; }
.react-flow__panel.right { right: 0; }
.react-flow__panel.center { left: 50%; transform: translateX(-50%); }
@keyframes dashdraw { from { stroke-dashoffset: 10; } }
@keyframes pulse-glow { 0%, 100% { filter: drop-shadow(0 0 6px rgba(245,158,11,0.3)); } 50% { filter: drop-shadow(0 0 16px rgba(245,158,11,0.6)); } }
@keyframes expand-in { from { transform: scale(0.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.scroll-node-glow { animation: pulse-glow 3s ease-in-out infinite; }
.expand-node { animation: expand-in 0.3s ease-out forwards; }
`;

if (typeof document !== 'undefined' && !document.getElementById('reactflow-styles-v2')) {
  const style = document.createElement('style');
  style.id = 'reactflow-styles-v2';
  style.textContent = REACTFLOW_STYLES;
  document.head.appendChild(style);
}

const API_URL = process.env.REACT_APP_BACKEND_URL;

// === CUSTOM NODE: CORNER Piece (Prominent — 4 foundational passages) ===
const CornerPieceNode = memo(({ data }) => {
  const [hovered, setHovered] = useState(false);
  const isExpanded = data.isExpanded;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`mindmap-piece-${data.pieceId}`}
      className={`relative transition-all duration-300 ${isExpanded ? '' : 'scroll-node-glow'}`}
      style={{ transform: hovered ? 'scale(1.12)' : 'scale(1)', transition: 'transform 0.3s ease' }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

      <div className="relative flex flex-col items-center justify-center rounded-2xl cursor-pointer"
        style={{
          width: 160,
          padding: '18px 12px',
          background: isExpanded
            ? `linear-gradient(135deg, ${data.color}35, ${data.color}12)`
            : `linear-gradient(145deg, rgba(35,25,12,0.97), rgba(22,16,8,0.95))`,
          border: `2.5px solid ${isExpanded ? data.color : `${data.color}80`}`,
          boxShadow: isExpanded
            ? `0 0 50px ${data.color}45, 0 0 100px ${data.color}15, 0 10px 40px rgba(0,0,0,0.5)`
            : hovered
              ? `0 0 35px ${data.color}35, 0 8px 30px rgba(0,0,0,0.45)`
              : `0 0 20px ${data.color}15, 0 6px 20px rgba(0,0,0,0.4)`,
        }}>

        {/* Corner number badge */}
        <div className="absolute -top-3 -right-3 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black text-white"
          style={{ backgroundColor: data.color, boxShadow: `0 0 12px ${data.color}70, 0 2px 8px rgba(0,0,0,0.4)` }}>
          {data.number}
        </div>

        {/* Corner label */}
        <p className="text-[7px] tracking-[0.35em] uppercase font-mono mb-2"
          style={{ color: `${data.color}90` }}>Corner Piece</p>

        {/* Scroll image — larger for corners */}
        <img src={SCROLL_IMG} alt="scroll" className="w-14 h-16 object-contain mb-2 drop-shadow-lg" draggable="false" />

        {/* Scripture reference */}
        <p className="text-[10px] font-serif font-semibold tracking-wide text-center leading-tight"
          style={{ color: data.color }}>
          {data.reference}
        </p>

        {/* Title preview */}
        <p className="text-[8px] font-serif text-center leading-tight mt-1.5 px-1"
          style={{ color: `${data.color}99` }}>
          {data.title}
        </p>

        {/* Hover tooltip */}
        {hovered && !isExpanded && (
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-full z-50 w-80 p-5 rounded-2xl border-2"
            style={{
              background: 'rgba(8,5,2,0.97)',
              borderColor: data.color,
              boxShadow: `0 16px 50px rgba(0,0,0,0.85), 0 0 30px ${data.color}30`,
            }}>
            <p className="text-sm font-serif font-bold mb-2" style={{ color: data.color }}>{data.title}</p>
            <p className="text-[13px] text-amber-100 font-serif italic leading-relaxed">"{data.anchor}"</p>
            <p className="text-[10px] text-amber-400 mt-3 font-mono tracking-wider font-semibold">CLICK TO EXPLORE CONNECTIONS</p>
          </div>
        )}
      </div>
    </div>
  );
});
CornerPieceNode.displayName = 'CornerPieceNode';

// === CUSTOM NODE: SPECIAL CHAPTER (Blueprints / Recapitulation) ===
const SpecialChapterNode = memo(({ data }) => {
  const [hovered, setHovered] = useState(false);
  const colors = data.chapterType === 'blueprints'
    ? { bg: '#1e3a5f', border: '#3b82f6', text: '#93c5fd', glow: 'rgba(59,130,246,0.3)' }
    : { bg: '#3b1f1f', border: '#ef4444', text: '#fca5a5', glow: 'rgba(239,68,68,0.3)' };

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => data.onSelect && data.onSelect(data.chapterType)}
      data-testid={`mindmap-special-${data.chapterType}`}
      className="relative transition-all duration-300 cursor-pointer"
      style={{ transform: hovered ? 'scale(1.12)' : 'scale(1)', transition: 'transform 0.3s ease' }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

      <div className="relative flex flex-col items-center justify-center rounded-2xl"
        style={{
          width: 150,
          padding: '16px 10px',
          background: `linear-gradient(145deg, ${colors.bg}, rgba(22,16,8,0.95))`,
          border: `2.5px solid ${colors.border}`,
          boxShadow: hovered
            ? `0 0 40px ${colors.glow}, 0 8px 30px rgba(0,0,0,0.5)`
            : `0 0 15px ${colors.glow}, 0 4px 15px rgba(0,0,0,0.4)`,
        }}>

        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[8px] font-mono uppercase tracking-widest"
          style={{ background: colors.border, color: '#fff', fontSize: '7px', letterSpacing: '2px' }}>
          Prerequisite
        </div>

        <svg className="w-8 h-8 mb-2" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="1.5">
          {data.chapterType === 'blueprints'
            ? <><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/><path d="M9 14l2 2 4-4"/></>
            : <><path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></>
          }
        </svg>

        <p className="text-[10px] font-serif font-bold text-center leading-tight"
          style={{ color: colors.text }}>
          {data.title}
        </p>

        <p className="text-[8px] text-center mt-1" style={{ color: `${colors.text}88` }}>
          Click to read
        </p>
      </div>
    </div>
  );
});
SpecialChapterNode.displayName = 'SpecialChapterNode';

// === CUSTOM NODE: BORDER Piece (Subtle — 6 supporting passages) ===
const BorderPieceNode = memo(({ data }) => {
  const [hovered, setHovered] = useState(false);
  const isExpanded = data.isExpanded;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      data-testid={`mindmap-piece-${data.pieceId}`}
      className="relative transition-all duration-300"
      style={{ transform: hovered ? 'scale(1.1)' : 'scale(1)', transition: 'transform 0.3s ease' }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />

      <div className="relative flex flex-col items-center justify-center rounded-lg cursor-pointer"
        style={{
          width: 100,
          padding: '10px 6px',
          background: isExpanded
            ? `linear-gradient(135deg, ${data.color}20, ${data.color}08)`
            : 'linear-gradient(145deg, rgba(25,18,8,0.9), rgba(18,12,5,0.85))',
          border: `1.5px solid ${isExpanded ? `${data.color}90` : `${data.color}35`}`,
          boxShadow: isExpanded
            ? `0 0 30px ${data.color}25, 0 6px 20px rgba(0,0,0,0.4)`
            : hovered
              ? `0 0 15px ${data.color}18, 0 4px 12px rgba(0,0,0,0.3)`
              : `0 2px 8px rgba(0,0,0,0.3)`,
        }}>

        {/* Small scroll image */}
        <img src={SCROLL_IMG} alt="scroll" className="w-8 h-9 object-contain mb-1 opacity-60" draggable="false" />

        {/* Scripture reference */}
        <p className="text-[8px] font-mono tracking-wide text-center leading-tight"
          style={{ color: `${data.color}77` }}>
          {data.reference}
        </p>

        {/* Hover tooltip */}
        {hovered && !isExpanded && (
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-y-full z-50 w-72 p-4 rounded-xl border-2"
            style={{
              background: 'rgba(8,5,2,0.97)',
              borderColor: `${data.color}90`,
              boxShadow: `0 12px 40px rgba(0,0,0,0.85), 0 0 20px ${data.color}20`,
            }}>
            <p className="text-[13px] font-serif font-bold mb-1.5" style={{ color: data.color }}>{data.title}</p>
            <p className="text-[12px] text-amber-100 font-serif italic leading-relaxed">"{data.anchor}"</p>
            <p className="text-[9px] text-amber-400 mt-2 font-mono tracking-wider font-semibold">CLICK TO EXPLORE</p>
          </div>
        )}
      </div>
    </div>
  );
});
BorderPieceNode.displayName = 'BorderPieceNode';

// === CUSTOM NODE: Center Book ===
const CenterBookNode = memo(({ data }) => {
  return (
    <div className="text-center" data-testid="mindmap-center-node">
      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <div className="px-6 py-5 rounded-2xl border-2"
        style={{
          background: 'rgba(8,5,2,0.93)',
          borderColor: 'rgba(217,119,6,0.5)',
          boxShadow: '0 0 60px rgba(217,119,6,0.15), 0 0 120px rgba(0,0,0,0.5)',
        }}>
        <p className="text-[9px] tracking-[0.4em] uppercase font-serif mb-1" style={{ color: '#92400e' }}>Reading the Treasure Map</p>
        <h2 className="text-lg font-serif font-bold"
          style={{ background: 'linear-gradient(180deg, #fcd34d, #d97706)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          The King's Return
        </h2>
        <p className="text-[9px] text-amber-600/60 font-serif mt-1.5 italic">4 Corner Foundations</p>
        <p className="text-[8px] text-amber-600/60 font-serif mt-0.5">6 Border Supports</p>
      </div>
    </div>
  );
});
CenterBookNode.displayName = 'CenterBookNode';

// === CUSTOM NODE: Appendix ===
const AppendixNode = memo(({ data }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="expand-node cursor-pointer"
      data-testid={`mindmap-appendix-${data.appendixCode}`}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="target" position={Position.Bottom} style={{ opacity: 0 }} />
      <div className="px-4 py-3 rounded-xl transition-all duration-200"
        style={{
          background: hovered ? 'rgba(8,5,2,0.95)' : 'rgba(8,5,2,0.9)',
          border: `2px solid ${hovered ? data.color : `${data.color}70`}`,
          boxShadow: hovered ? `0 0 25px ${data.color}30, 0 8px 20px rgba(0,0,0,0.6)` : '0 4px 12px rgba(0,0,0,0.4)',
          maxWidth: 200,
        }}>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold font-mono px-2 py-1 rounded"
            style={{ backgroundColor: `${data.color}30`, color: data.color }}>
            {data.appendixCode}
          </span>
          <span className="text-[11px] font-serif font-medium leading-tight" style={{ color: '#fcd34d' }}>
            {data.title}
          </span>
        </div>
      </div>
    </div>
  );
});
AppendixNode.displayName = 'AppendixNode';

const nodeTypes = {
  cornerPiece: CornerPieceNode,
  borderPiece: BorderPieceNode,
  centerBook: CenterBookNode,
  appendixNode: AppendixNode,
  specialChapter: SpecialChapterNode,
};

// === FRAME LAYOUT — Corners prominent, borders subtle ===
const FRAME = {
  width: 1000,
  height: 600,
  centerX: 500,
  centerY: 300,
};

function getFramePositions() {
  const { width, height } = FRAME;
  const mx = width / 2;
  const my = height / 2;

  return {
    // Corners — placed at the 4 extremes, large and visible
    'corner-1': { x: 0, y: 0 },
    'corner-2': { x: width, y: 0 },
    'corner-3': { x: width, y: height },
    'corner-4': { x: 0, y: height },
    // Borders — positioned along edges, tucked closer together
    'border-2': { x: mx * 0.55, y: -20 },        // top-left
    'border-3': { x: mx * 1.45, y: -20 },         // top-right
    'border-6': { x: width + 30, y: my },          // right
    'border-4': { x: -30, y: my },                 // left
    'border-5': { x: mx * 0.55, y: height + 20 }, // bottom-left
    'border-1': { x: mx * 1.45, y: height + 20 }, // bottom-right
    center: { x: mx - 80, y: my - 40 },
    // Special chapters — outside the corners as prerequisites
    'blueprints': { x: -200, y: -120 },
    'recapitulation': { x: width + 120, y: height + 80 },
  };
}

// === MAIN COMPONENT ===
function MindMap() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pieces, setPieces] = useState([]);
  const [crossRefs, setCrossRefs] = useState(null);
  const [specialChapters, setSpecialChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPiece, setExpandedPiece] = useState(null);
  const [selectedSpecialChapter, setSelectedSpecialChapter] = useState(null);
  const [specialChapterContent, setSpecialChapterContent] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Fetch puzzle pieces + cross-references + special chapters
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [piecesRes, refsRes, specialRes] = await Promise.all([
          fetch(`${API_URL}/api/puzzle/pieces`),
          fetch(`${API_URL}/api/mindmap/cross-references`),
          fetch(`${API_URL}/api/admin/appendices/public/special-chapters`),
        ]);
        const piecesData = await piecesRes.json();
        const refsData = await refsRes.json();
        setPieces(piecesData.pieces || []);
        setCrossRefs(refsData);
        if (specialRes.ok) {
          const specialData = await specialRes.json();
          setSpecialChapters(specialData.chapters || []);
        }
      } catch (err) {
        console.error('Failed to fetch mind map data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Build graph whenever data or expanded piece changes
  useEffect(() => {
    if (pieces.length === 0) return;
    const handleSpecialSelect = async (chapterType) => {
      setSelectedSpecialChapter(chapterType);
      try {
        const res = await fetch(`${API_URL}/api/admin/appendices/public/special-chapters/${chapterType}`);
        if (res.ok) {
          const data = await res.json();
          setSpecialChapterContent(data);
        }
      } catch (err) {
        console.error('Failed to fetch special chapter:', err);
      }
    };
    const { graphNodes, graphEdges } = buildProgressiveGraph(pieces, crossRefs, expandedPiece, specialChapters, handleSpecialSelect);
    setNodes(graphNodes);
    setEdges(graphEdges);
  }, [pieces, crossRefs, expandedPiece, specialChapters, setNodes, setEdges]);

  const onNodeClick = useCallback((event, node) => {
    if (node.type === 'cornerPiece' || node.type === 'borderPiece') {
      const pieceId = node.data.pieceId;
      // Toggle expansion
      setExpandedPiece(prev => prev === pieceId ? null : pieceId);
    } else if (node.type === 'appendixNode') {
      // Navigate to ebook reader
      if (node.data.bookId && node.data.order) {
        navigate(`/ebooks/${node.data.bookId}/${node.data.order}`);
      }
    }
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: '#0a0704' }} data-testid="mindmap-loading">
        <div className="text-center">
          <img src={SCROLL_IMG} alt="" className="w-16 h-20 object-contain mx-auto mb-4 animate-pulse" />
          <p className="text-amber-200/70 text-lg font-serif">Preparing the Knowledge Map...</p>
        </div>
      </div>
    );
  }

  const night = isNightTime();
  const bgImage = night ? NIGHT_BG : DAY_BG;
  const bgFallback = night ? '#060503' : '#1a3050';

  return (
    <div className="h-screen w-full relative overflow-hidden" data-testid="mindmap-container"
      style={{ background: bgFallback }}>
      {/* Background image layer — scaled up to fill and hide torn edges */}
      <div className="absolute z-0"
        style={{
          inset: '-20%',
          width: '140%',
          height: '140%',
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }} />
      {/* Vignette overlay */}
      <div className="absolute inset-0 z-0"
        style={{
          background: night
            ? 'radial-gradient(ellipse at center, rgba(3,2,1,0.35) 0%, rgba(3,2,1,0.75) 70%, rgba(3,2,1,0.95) 100%)'
            : 'radial-gradient(ellipse at center, rgba(20,14,8,0.15) 0%, rgba(20,14,8,0.45) 50%, rgba(10,7,4,0.85) 100%)',
        }} />

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.4 }}
        minZoom={0.3}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        style={{ background: 'transparent', position: 'relative', zIndex: 1 }}
      >
        <Controls className="!bg-slate-900 !border-amber-900/30 !shadow-xl" />

        {/* Top nav panel */}
        <div className="absolute top-4 left-4 z-50 flex items-center gap-3" data-testid="mindmap-nav">
          <button
            data-testid="btn-puzzle-explorer"
            onClick={() => navigate('/puzzle')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-serif transition-all duration-300 border border-amber-800/30 text-amber-500 hover:text-amber-300 hover:border-amber-600/50"
            style={{ background: 'rgba(15,10,5,0.9)', backdropFilter: 'blur(8px)' }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10l-2 1m0 0l-2-1m2 1v2.5M20 7l-2 1m2-1l-2-1m2 1v2.5M14 4l-2-1-2 1M4 7l2-1M4 7l2 1M4 7v2.5M12 21l-2-1m2 1l2-1m-2 1v-2.5M6 18l-2-1v-2.5M18 18l2-1v-2.5" />
            </svg>
            Treasure Map
          </button>
          <button
            data-testid="btn-back-library"
            onClick={() => navigate('/library')}
            className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-serif transition-all duration-300 border border-amber-900/20 text-amber-500 hover:text-amber-300 hover:border-amber-700/40"
            style={{ background: 'rgba(15,10,5,0.9)', backdropFilter: 'blur(8px)' }}
          >
            Library
          </button>
        </div>

        {/* Buy the Book CTA — positioned below nav to avoid overlap */}
        <div className="absolute bottom-20 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:bottom-auto sm:top-16 sm:left-4 z-50" data-testid="mindmap-buy-cta">
          <button
            data-testid="btn-mindmap-buy"
            onClick={() => navigate('/buy')}
            className="flex items-center gap-2.5 px-5 py-2.5 rounded-full text-sm font-serif font-bold transition-all duration-300 hover:scale-105 border"
            style={{
              background: 'linear-gradient(135deg, rgba(180,83,9,0.95), rgba(217,119,6,0.95))',
              borderColor: 'rgba(252,211,77,0.4)',
              color: '#fff',
              boxShadow: '0 4px 20px rgba(217,119,6,0.35), 0 0 40px rgba(217,119,6,0.1)',
              backdropFilter: 'blur(8px)',
            }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Buy the Book
          </button>
        </div>

        {/* Instructions — raised above "Made with Emergent" badge */}
        <div className="absolute bottom-16 right-4 z-50 hidden sm:block" data-testid="mindmap-help">
          <div className="px-4 py-3 rounded-xl border border-amber-900/20 max-w-xs"
            style={{ background: 'rgba(15,10,5,0.9)', backdropFilter: 'blur(8px)' }}>
            <p className="text-[9px] tracking-[0.2em] uppercase text-amber-700/50 mb-2 font-mono">How to Read the Map</p>
            <div className="space-y-1 text-[10px] text-amber-200/40 font-serif">
              <p><span className="text-amber-400">Large scrolls</span> are the 4 corner foundations</p>
              <p><span className="text-amber-600/60">Small scrolls</span> are the 6 border supports</p>
              <p><span className="text-amber-400">Click</span> any scroll to reveal connections</p>
              <p><span className="text-emerald-400">Click an appendix</span> to read the full study</p>
              <p className="text-amber-600/30 pt-1">Scroll to zoom — Drag to pan</p>
            </div>
          </div>
        </div>

        {expandedPiece && (
          <div className="absolute top-4 right-4 z-50">
            <button
              data-testid="btn-collapse-all"
              onClick={() => setExpandedPiece(null)}
              className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-serif border border-amber-800/30 text-amber-500 hover:text-amber-300 transition-all"
              style={{ background: 'rgba(15,10,5,0.9)' }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Collapse
            </button>
          </div>
        )}
      </ReactFlow>

      {/* Special Chapter Reader Modal */}
      {selectedSpecialChapter && specialChapterContent && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80" data-testid="special-chapter-modal"
          onClick={() => { setSelectedSpecialChapter(null); setSpecialChapterContent(null); }}>
          <div className="relative w-full max-w-2xl max-h-[80vh] m-4 rounded-2xl border-2 overflow-hidden"
            style={{
              background: 'linear-gradient(180deg, #12100d, #0a0806)',
              borderColor: selectedSpecialChapter === 'blueprints' ? '#3b82f6' : '#ef4444',
            }}
            onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between"
              style={{ borderColor: selectedSpecialChapter === 'blueprints' ? 'rgba(59,130,246,0.3)' : 'rgba(239,68,68,0.3)' }}>
              <div>
                <p className="text-[9px] tracking-[0.3em] uppercase font-mono"
                  style={{ color: selectedSpecialChapter === 'blueprints' ? '#93c5fd' : '#fca5a5' }}>
                  {selectedSpecialChapter === 'blueprints' ? 'Blueprints' : 'Recapitulation'} — Prerequisite Reading
                </p>
                <h2 className="text-lg font-serif text-amber-100">{specialChapterContent.title}</h2>
              </div>
              <button
                onClick={() => { setSelectedSpecialChapter(null); setSpecialChapterContent(null); }}
                className="text-amber-600/50 hover:text-amber-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5 overflow-y-auto" style={{ maxHeight: 'calc(80vh - 100px)' }}>
              {specialChapterContent.laymen_summary && (
                <div className="mb-5 p-4 rounded-xl bg-amber-900/10 border border-amber-800/20">
                  <p className="text-xs font-mono uppercase tracking-wider text-amber-600/50 mb-2">Summary</p>
                  <p className="text-sm font-serif text-amber-200/80 leading-relaxed">{specialChapterContent.laymen_summary}</p>
                </div>
              )}
              <div className="text-sm font-serif text-amber-100/70 leading-relaxed whitespace-pre-wrap">
                {specialChapterContent.content || 'Full content available with Scholar access.'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// === BUILD THE PROGRESSIVE GRAPH ===
function buildProgressiveGraph(pieces, crossRefs, expandedPieceId, specialChapters = [], onSpecialSelect = null) {
  const graphNodes = [];
  const graphEdges = [];
  const positions = getFramePositions();

  // Keyword → color map
  const KEYWORD_COLORS = {
    BEREAN: '#3B82F6', TROUBLED: '#EF4444', ENESTEKEN: '#DC2626',
    OLIVET: '#F59E0B', ELECT: '#10B981', TRUMPET: '#8B5CF6',
    MILLENNIAL: '#6366F1', ARCHITECTURE: '#0EA5E9', PAROUSIA: '#7C3AED',
    PERSECUTION: '#E11D48', WRATH: '#BE123C', SIGNLESS: '#A855F7',
    COMFORT: '#14B8A6', FATHERS: '#78716C', IMMINENCY: '#EA580C',
    WALL: '#DC2626', PHILADELPHIA: '#2563EB', OVERCOMER: '#22C55E',
    HOPE: '#F97316', RECORD: '#64748B', ACCOUNT: '#475569',
    DANIEL: '#CA8A04', TABERNACLES: '#059669', DELIVERED: '#16A34A',
  };

  // 1. Center node
  graphNodes.push({
    id: 'center',
    type: 'centerBook',
    position: positions.center,
    data: {},
    draggable: false,
  });

  // 1b. Special chapter nodes (Blueprints & Recapitulation) — prerequisites outside corners
  specialChapters.forEach((ch) => {
    const pos = positions[ch.chapter_type];
    if (!pos) return;
    graphNodes.push({
      id: `special-${ch.chapter_type}`,
      type: 'specialChapter',
      position: pos,
      data: {
        chapterType: ch.chapter_type,
        title: ch.title || (ch.chapter_type === 'blueprints' ? 'Blueprints' : 'Recapitulation'),
        onSelect: onSpecialSelect,
      },
      draggable: false,
    });
  });

  // 1c. Prerequisite edges: special chapters → corners
  if (specialChapters.length > 0) {
    const hasBlueprints = specialChapters.some(c => c.chapter_type === 'blueprints');
    const hasRecap = specialChapters.some(c => c.chapter_type === 'recapitulation');
    if (hasBlueprints) {
      ['corner-1', 'corner-4'].forEach(cid => {
        graphEdges.push({
          id: `prereq-blueprints-${cid}`,
          source: 'special-blueprints',
          target: cid,
          style: { stroke: 'rgba(59,130,246,0.35)', strokeWidth: 2, strokeDasharray: '6 4' },
          animated: true,
          label: '',
        });
      });
    }
    if (hasRecap) {
      ['corner-2', 'corner-3'].forEach(cid => {
        graphEdges.push({
          id: `prereq-recap-${cid}`,
          source: 'special-recapitulation',
          target: cid,
          style: { stroke: 'rgba(239,68,68,0.35)', strokeWidth: 2, strokeDasharray: '6 4' },
          animated: true,
          label: '',
        });
      });
    }
  }

  // 2. Puzzle piece nodes — corner vs border node types
  pieces.forEach((piece) => {
    const pos = positions[piece.piece_id];
    if (!pos) return;

    const isCorner = piece.piece_type === 'corner';

    graphNodes.push({
      id: piece.piece_id,
      type: isCorner ? 'cornerPiece' : 'borderPiece',
      position: pos,
      data: {
        pieceId: piece.piece_id,
        pieceType: piece.piece_type,
        title: piece.title,
        reference: piece.reference,
        anchor: piece.anchor,
        keyword: piece.keyword,
        color: piece.color,
        number: piece.number,
        isExpanded: expandedPieceId === piece.piece_id,
      },
    });
  });

  // 3. Connection edges between pieces (connects_to)
  pieces.forEach((piece) => {
    const isCornerSource = piece.piece_type === 'corner';
    (piece.connects_to || []).forEach((targetId) => {
      const edgeId = `conn-${piece.piece_id}-${targetId}`;
      const reverseId = `conn-${targetId}-${piece.piece_id}`;
      if (!graphEdges.find(e => e.id === edgeId || e.id === reverseId)) {
        const targetPiece = pieces.find(p => p.piece_id === targetId);
        const isCornerTarget = targetPiece?.piece_type === 'corner';
        const bothCorners = isCornerSource && isCornerTarget;
        graphEdges.push({
          id: edgeId,
          source: piece.piece_id,
          target: targetId,
          style: {
            stroke: bothCorners ? 'rgba(217,119,6,0.5)' : 'rgba(217,119,6,0.2)',
            strokeWidth: bothCorners ? 2.5 : 1.5,
          },
          animated: false,
        });
      }
    });
  });

  // 4. If a piece is expanded, show its related appendices
  if (expandedPieceId && crossRefs) {
    const expandedPiece = pieces.find(p => p.piece_id === expandedPieceId);
    if (expandedPiece) {
      const keyword = expandedPiece.keyword;
      const piecePos = positions[expandedPieceId];

      // Collect appendices: primary = exact keyword match, secondary = from related chapters
      const relatedAppendices = [];
      const seenIds = new Set();
      const chapters = crossRefs.cross_references || [];

      // First pass: exact keyword match (primary connections)
      chapters.forEach((ch) => {
        ch.connections.forEach((conn) => {
          if (conn.keyword === keyword && !seenIds.has(conn.target_chapter_id)) {
            seenIds.add(conn.target_chapter_id);
            relatedAppendices.push({ ...conn, isPrimary: true });
          }
        });
      });

      // Second pass: appendices from chapters that ALSO reference this keyword
      chapters.forEach((ch) => {
        const hasKeyword = ch.connections.some(c => c.keyword === keyword);
        if (hasKeyword) {
          ch.connections.forEach((conn) => {
            if (!seenIds.has(conn.target_chapter_id)) {
              seenIds.add(conn.target_chapter_id);
              relatedAppendices.push({ ...conn, isPrimary: false });
            }
          });
        }
      });

      // Limit to 8 max for readability
      const displayApps = relatedAppendices.slice(0, 8);

      // Calculate arc direction: OUTWARD from frame center
      const dx = piecePos.x - FRAME.centerX + 80;
      const dy = piecePos.y - FRAME.centerY + 40;
      const baseAngle = Math.atan2(dy, dx);
      const radius = 220;
      const arcSpread = Math.min(Math.PI * 1.0, displayApps.length * 0.35);

      displayApps.forEach((app, i) => {
        const angle = displayApps.length === 1
          ? baseAngle
          : baseAngle - arcSpread / 2 + (i / (displayApps.length - 1)) * arcSpread;
        const appColor = KEYWORD_COLORS[app.keyword] || '#64748B';
        const appId = `app-${app.target_chapter_id}`;

        graphNodes.push({
          id: appId,
          type: 'appendixNode',
          position: {
            x: piecePos.x + radius * Math.cos(angle),
            y: piecePos.y + radius * Math.sin(angle),
          },
          data: {
            appendixCode: app.appendix_code || app.keyword,
            title: (app.target_chapter_title || '').replace(/^Appendix [A-Z]{2}:?\s*/, '').substring(0, 45),
            color: appColor,
            bookId: app.target_book_id,
            order: app.target_chapter_order || 1,
          },
        });

        graphEdges.push({
          id: `edge-${expandedPieceId}-${appId}`,
          source: expandedPieceId,
          target: appId,
          style: {
            stroke: appColor,
            strokeWidth: app.isPrimary ? 3 : 2,
            opacity: app.isPrimary ? 0.9 : 0.6,
          },
          animated: true,
          markerEnd: { type: MarkerType.ArrowClosed, color: appColor, width: 14, height: 14 },
        });
      });

      // Highlight piece-to-piece connections
      graphEdges.forEach((edge) => {
        if ((edge.source === expandedPieceId || edge.target === expandedPieceId) && !edge.id.startsWith('edge-')) {
          edge.style = { ...edge.style, stroke: expandedPiece.color, strokeWidth: 2.5, opacity: 0.7 };
          edge.animated = true;
        }
      });
    }
  }

  return { graphNodes, graphEdges };
}

export default MindMap;
